import React, { useMemo, useState } from "react";
import {
  Box,
  // Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoBannerCst from "@components/InfoBannerCst";
import { useParams } from "react-router-dom";
import {
  // ListFilter,
  Search,
} from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
import { WizardStepProps } from "@components/WizardCst/types";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { mapAggregatedType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { useLOSearch } from "@pages/Contents/V2/Corporate/Catalog/search/useLOSearch";
// import { useLOFilterableFields } from "@pages/Contents/V2/Corporate/Catalog/search/useLOFilterableFields";
// import SearchFiltersAccordion from "@pages/Contents/V2/Corporate/Catalog/search/SearchFiltersAccordion";
import {
  buildSearchConditions,
  LO_SORT_OPTION_KEYS,
  LO_SORT_ORDER_BY,
} from "@pages/Contents/V2/Corporate/Catalog/search/types";
import { EnrollmentStepData } from "../types";
import { StepContainer } from "../../Wizard/components/StepLayout";
import SortMenuCst from "@components/SortMenuCst";

const PAGE_SIZE = 10;

const EnrollStep: React.FC<WizardStepProps<EnrollmentStepData>> = ({
  data,
  setData,
}) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<string | null>("id-desc");
  // const [accordionFilters, setAccordionFilters] = useState<Record<string, any>>(
  //   {},
  // );
  // const [filtersOpen, setFiltersOpen] = useState(false);

  // const { fields: filterableFields } = useLOFilterableFields(corporateId);

  // const filters = useMemo(
  //   () => filterableFields.filter((f) => f.field !== "standalone"),
  //   [filterableFields],
  // );

  // const hasActiveFilters = useMemo(() => {
  //   const hasValue = (val: any): boolean => {
  //     if (val === undefined || val === null || val === "") return false;
  //     if (Array.isArray(val)) return val.length > 0;
  //     if (typeof val === "object") return Object.values(val).some(hasValue);
  //     return true;
  //   };
  //   return Object.values(accordionFilters || {}).some(hasValue);
  // }, [accordionFilters]);

  // const filtersHighlighted = hasActiveFilters || filtersOpen;

  const debouncedSearch = useDebouncedValue(search, 400);

  const conditions = useMemo(
    () =>
      buildSearchConditions({
        includeDrafts: false,
        standaloneOnly: true,
        excludeObsolete: true,
        // fields: filterableFields,
        // filterValues: accordionFilters,
        fields: [],
        filterValues: {},
      }),
    // [filterableFields, accordionFilters],
    [],
  );

  const { items, totalItems, totalPages, isLoading } = useLOSearch({
    corporateId,
    conditions,
    searchText: debouncedSearch,
    orderBy: LO_SORT_ORDER_BY[sort ?? ""] ?? LO_SORT_ORDER_BY["id-desc"],
    page,
    pageSize,
    enabled: !!corporateId,
  });

  const selectedIds = new Set(data.loSelections.map((s) => s.id));

  const toggle = (id: number, type: string, next: boolean) => {
    setData((prev) => {
      const list = prev.loSelections.filter((s) => s.id !== id);
      if (next) list.push({ id, type });
      return { loSelections: list };
    });
  };

  const sortOptions = LO_SORT_OPTION_KEYS.map((key) => ({
    value: key,
    label: String(tCatalog(`sort.${key}`)),
  }));

  return (
    <StepContainer>
      <InfoBannerCst>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: "inherit" }}
        >
          {t("enroll.notice")}
        </Typography>
      </InfoBannerCst>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 240,
            "& .MuiFormControl-root": {
              minWidth: "0 !important",
              width: "100% !important",
            },
            "& .MuiOutlinedInput-root": { borderRadius: "8px" },
            "& .MuiFormHelperText-root": { display: "none" },
          }}
        >
          <InputCst
            id="group-lo-search"
            placeholder={t("search")}
            inputSearch
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            startAdornment={
              <Search
                size={16}
                color={theme.palette.systemSecondary01}
                style={{ marginRight: 8 }}
              />
            }
          />
        </Box>
        {/* <Button
          variant="outlined"
          startIcon={<ListFilter size={16} />}
          onClick={() => setFiltersOpen((prev) => !prev)}
          sx={{
            height: "40px",
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            flexShrink: 0,
            transition: "none",
            borderColor: filtersHighlighted
              ? theme.palette.primary.main
              : theme.palette.divider,
            color: filtersHighlighted
              ? theme.palette.primary.main
              : theme.palette.text.primary,
            backgroundColor: filtersHighlighted
              ? theme.palette.action.hover
              : "transparent",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {tCatalog("filters-button")}
        </Button> */}
        <SortMenuCst
          id="group-lo-sort"
          value={sort ?? "id-desc"}
          options={sortOptions}
          onChange={(next) => {
            setSort(next || null);
            setPage(1);
          }}
        />
      </Box>

      {/* <SearchFiltersAccordion
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        fields={filters}
        value={accordionFilters}
        onApply={(next) => {
          setAccordionFilters(next);
          setPage(1);
        }}
      /> */}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
            color: theme.palette.text.tertiary,
          }}
        >
          {t("emptyLo")}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((raw: any) => {
            const entity = raw.lo;
            const entityType = mapAggregatedType(raw.type);
            const cardProps = mapEntityToCard(entity, entityType, {
              t: tCatalog,
              onDetails: () => {},
              hideStandaloneBadge: true,
            });
            return (
              <ListItemCardCst
                key={`${entityType}-${entity.id}`}
                {...cardProps}
                actions={[]}
                selectable
                selected={selectedIds.has(entity.id)}
                onSelect={(next) => toggle(entity.id, entityType, next)}
              />
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </Box>
      )}
    </StepContainer>
  );
};

export default EnrollStep;
